import { useQueryClient } from '@tanstack/react-query';

import { requestBuilder } from '../../../http/RequestBuilder';

export interface MembershipComplianceNotes {
    id: string;
    membershipId: string;
    notes: string | null;
    createdAt: string;
    updatedAt: string | null;
}

export interface UpsertMembershipComplianceNotesBody {
    membershipId: string;
    notes: string | null;
}

export const getMembershipComplianceNotesQueryKey = ({
    membershipId,
}: {
    membershipId: string | null;
}): string => `getMembershipComplianceNotes/${membershipId ?? ''}`;

const useGetMembershipComplianceNotesQuery = requestBuilder.query<
    MembershipComplianceNotes,
    { membershipId: string | null }
>({
    endpoint: ({ membershipId }) =>
        `/api/admin/membership-compliance-notes/${membershipId}`,
    queryKey: getMembershipComplianceNotesQueryKey,
});

const useUpsertMembershipComplianceNotesMutation = requestBuilder.mutation<
    MembershipComplianceNotes,
    UpsertMembershipComplianceNotesBody
>({
    endpoint: '/api/admin/membership-compliance-notes',
    method: 'POST',
});

export const useMembershipComplianceNotes = (membershipId: string | null) => {
    const queryClient = useQueryClient();
    const complianceNotesQueryKey = getMembershipComplianceNotesQueryKey({
        membershipId,
    });

    const queryResult = useGetMembershipComplianceNotesQuery({ membershipId });

    const { mutateAsync, isPending } =
        useUpsertMembershipComplianceNotesMutation({
            onMutate: async () => {
                await queryClient.cancelQueries({
                    queryKey: [complianceNotesQueryKey],
                    exact: true,
                });
            },
            onSuccess: async () => {
                await queryClient.invalidateQueries({
                    queryKey: [complianceNotesQueryKey],
                    exact: true,
                });
            },
        });

    const upsertMembershipComplianceNotesAsync = async (
        body: UpsertMembershipComplianceNotesBody,
    ) => {
        return await mutateAsync({
            body,
        });
    };

    return {
        ...queryResult,
        upsertMembershipComplianceNotesAsync,
        isPending,
    };
};
